import { useState, useCallback } from "react";
import {
  ApiDataResponse,
  ApiResponse,
  Class,
  Level,
} from "../../services/Dtos";
import { ApiService } from "../../services/ApiService";
import useAxios from "../useAxios";
import { useSchoolsContext } from "../../Contexts/schoolContext";
import { useApiResponseHandler } from "../useApiResponseHandler";

export const useClassService = () => {
  const { setClasses } = useSchoolsContext();
  const axiosClient = useAxios();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  // Class selection states
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [classToEdit, setClassToEdit] = useState<Class | null>(null);
  const [classLevel, setClassLevel] = useState<Level | null>(null);
  const [selectedClassSubjects, setSelectedClassSubjects] = useState<string[]>(
    []
  );
  const [selectedClassBehavioralSubjects, setSelectedClassBehavioralSubjects] =
    useState<string[]>([]);
  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const { handleApiError, handleApiSuccess, clearErrors } =
    useApiResponseHandler({
      setErrors,
    });

  const createClass = useCallback(
    async (
      classRoom: Class,
      onSuccess?: (classRoom: Class) => void
    ): Promise<{ result: ApiDataResponse<Class> }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Class>(
          "/classes",
          axiosClient
        ).create(classRoom);
        if (result.succeeded) {
          setClasses((prev: Class[]) =>
            [...prev, result.data].sort((a, b) => a.order - b.order)
          );
          handleApiSuccess("Class created successfully");
          onSuccess?.(classRoom);
          return { result };
        } else {
          handleApiError(result, "Failed to create class");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to create class");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to create class",
            statusCode: 500,
            errors: error.response?.data.errors || [],
            data: null as any,
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setClasses]
  );

  const updateClass = useCallback(
    async (
      id: string,
      classRoom: Class,
      onSuccess?: (classRoom: Class) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Class>(
          "/classes",
          axiosClient
        ).update(id, classRoom);
        if (result.succeeded) {
          setClasses((prev: Class[]) =>
            prev.map((c) => (c.id === id ? classRoom : c))
          );
          handleApiSuccess("Class updated successfully");
          onSuccess?.(classRoom);
          return { result };
        } else {
          handleApiError(result, "Failed to update class");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to update class");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to update class",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
      }
    },
    [handleApiError, clearErrors, handleApiSuccess, setClasses]
  );

  const deleteClass = useCallback(
    async (
      id: string,
      onSuccess?: (id: string) => void
    ): Promise<{ result: ApiResponse }> => {
      setIsLoading(true);
      clearErrors();

      try {
        const result = await new ApiService<Class>(
          "/classes",
          axiosClient
        ).delete(id);
        if (result.succeeded) {
          setClasses((prev: Class[]) => prev.filter((c) => c.id !== id));
          handleApiSuccess("Class deleted successfully");

          // Reset selected class if the deleted class was selected
          if (selectedClass?.id === id) {
            setSelectedClass(null);
            setSelectedClassSubjects([]);
            setSelectedClassBehavioralSubjects([]);
            setUnsavedChanges(false);
          }

          // Reset class to edit if the deleted class was being edited
          if (classToEdit?.id === id) {
            setClassToEdit(null);
          }

          onSuccess?.(id);
          return { result };
        } else {
          handleApiError(result, "Failed to delete class");
          return { result };
        }
      } catch (error: any) {
        const errorMessage = handleApiError(error, "Failed to delete class");
        return {
          result: {
            succeeded: false,
            message: errorMessage?.message || "Failed to delete class",
            statusCode: 500,
            errors: error.response?.data.errors || [],
          },
        };
      } finally {
        setIsLoading(false);
        setOpenDeleteDialog(false);
      }
    },
    [
      handleApiError,
      clearErrors,
      handleApiSuccess,
      setClasses,
      selectedClass,
      classToEdit,
    ]
  );

  return {
    createClass,
    updateClass,
    deleteClass,
    isLoading,
    errors,
    clearErrors,
    openDeleteDialog,
    setOpenDeleteDialog,
    // Class selection states
    selectedClass,
    setSelectedClass,
    classToEdit,
    setClassToEdit,
    classLevel,
    setClassLevel,
    selectedClassSubjects,
    setSelectedClassSubjects,
    selectedClassBehavioralSubjects,
    setSelectedClassBehavioralSubjects,
    unsavedChanges,
    setUnsavedChanges,
  };
};
